import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Sparkles, Mic, CheckSquare, Download, Share2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const tourSteps = [
  {
    id: "welcome",
    title: "¡Bienvenido a Minut-IA!",
    description: "Te mostraremos cómo usar la aplicación en solo 6 pasos. ¿Empezamos?",
    icon: Sparkles,
    position: "center"
  },
  {
    id: "new-meeting",
    title: "Crear Reunión",
    description: "Haz clic aquí para grabar o subir el audio de tu reunión. La IA generará la minuta automáticamente.",
    icon: Mic,
    target: "[data-tour='new-meeting']",
    position: "bottom"
  },
  {
    id: "chat-ia",
    title: "Chat-IA 🤖",
    description: "Pregunta sobre tus reuniones, tareas o decisiones. El asistente de IA responde basándose en tus minutas.",
    icon: MessageCircle,
    target: "[data-testid='nav-chat']",
    position: "bottom"
  },
  {
    id: "meetings",
    title: "Tus Reuniones",
    description: "Aquí aparecerán todas tus reuniones. Haz clic en cualquiera para ver la minuta completa.",
    icon: Sparkles,
    target: "[data-tour='meetings-grid']",
    position: "top"
  },
  {
    id: "tasks",
    title: "Gestionar Tareas",
    description: "Las tareas identificadas aparecen aquí. Puedes cambiar su estado: Pendiente → En proceso → Completada.",
    icon: CheckSquare,
    target: "[data-tour='tasks-section']",
    position: "top"
  },
  {
    id: "finish",
    title: "¡Listo!",
    description: "Ya conoces lo básico. Desde cada reunión podrás descargar y compartir las minutas. ¡A trabajar!",
    icon: Share2,
    position: "center"
  }
];

const ProductTour = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = tourSteps[currentStep];
  const Icon = step.icon;
  const isFirst = currentStep === 0;
  const isLast = currentStep === tourSteps.length - 1;

  useEffect(() => {
    if (step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [currentStep, step.target]);

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const getTooltipPosition = () => {
    if (!targetRect || step.position === 'center') {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const padding = 20;
    const tooltipWidth = 384; // max-w-sm is approximately 384px
    const tooltipHeight = 300; // estimated tooltip height
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 16; // minimum margin from edges
    
    let top, left, transform;
    
    if (step.position === 'bottom') {
      top = `${Math.min(targetRect.bottom + padding, viewportHeight - tooltipHeight - margin)}px`;
      left = `${targetRect.left + targetRect.width / 2}px`;
      transform = 'translateX(-50%)';
      
      // Adjust horizontal position if tooltip goes off-screen
      const estimatedLeft = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      if (estimatedLeft < margin) {
        left = `${margin + tooltipWidth / 2}px`;
      } else if (estimatedLeft + tooltipWidth > viewportWidth - margin) {
        left = `${viewportWidth - margin - tooltipWidth / 2}px`;
      }
    } else if (step.position === 'top') {
      top = `${Math.max(targetRect.top - padding, margin + tooltipHeight)}px`;
      left = `${targetRect.left + targetRect.width / 2}px`;
      transform = 'translate(-50%, -100%)';
      
      // Adjust horizontal position if tooltip goes off-screen
      const estimatedLeft = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      if (estimatedLeft < margin) {
        left = `${margin + tooltipWidth / 2}px`;
      } else if (estimatedLeft + tooltipWidth > viewportWidth - margin) {
        left = `${viewportWidth - margin - tooltipWidth / 2}px`;
      }
    } else {
      // Default center
      top = '50%';
      left = '50%';
      transform = 'translate(-50%, -50%)';
    }

    return { top, left, transform };
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100]">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60"
          onClick={handleSkip}
        />

        {/* Highlight target element */}
        {targetRect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute bg-white rounded-lg shadow-2xl ring-4 ring-orange-500 ring-offset-2"
            style={{
              top: targetRect.top - 8,
              left: targetRect.left - 8,
              width: targetRect.width + 16,
              height: targetRect.height + 16,
              pointerEvents: 'none'
            }}
          />
        )}

        {/* Tooltip */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="absolute bg-white rounded-xl shadow-2xl p-6 max-w-sm z-10"
          style={getTooltipPosition()}
        >
          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-3 right-3 text-stone-400 hover:text-stone-600"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-4">
            <Icon className="w-6 h-6 text-white" />
          </div>

          {/* Content */}
          <h3 className="text-lg font-semibold text-stone-900 mb-2">
            {step.title}
          </h3>
          <p className="text-stone-600 text-sm mb-6">
            {step.description}
          </p>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {tourSteps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentStep ? 'bg-orange-500' : 'bg-stone-200'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              disabled={isFirst}
              className={isFirst ? 'invisible' : ''}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            
            <Button
              size="sm"
              onClick={handleNext}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {isLast ? 'Finalizar' : 'Siguiente'}
              {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>

          {/* Skip link */}
          {!isLast && (
            <button
              onClick={handleSkip}
              className="block w-full text-center text-xs text-stone-400 hover:text-stone-600 mt-3"
            >
              Saltar tutorial
            </button>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Hook to manage tour state
export const useTour = () => {
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    // Check if user has seen the tour
    const hasSeenTour = localStorage.getItem('minut-ia-tour-completed');
    if (!hasSeenTour) {
      // Delay tour start to let the page load
      const timer = setTimeout(() => setShowTour(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const completeTour = () => {
    localStorage.setItem('minut-ia-tour-completed', 'true');
    setShowTour(false);
  };

  const resetTour = () => {
    localStorage.removeItem('minut-ia-tour-completed');
    setShowTour(true);
  };

  return { showTour, completeTour, resetTour };
};

export default ProductTour;
